import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaBell } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import NotificationDropdown from './NotificationDropdown';
import log from '../utils/logger';

const NotificationBell = ({ notifications, onClearNotifications, user, setNotifications, updateNotifications }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const refreshNotificationsFromStorage = () => {
    if (user?.sub && setNotifications) {
      const stored = localStorage.getItem(`notifications_${user.sub}`);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setNotifications(parsed);
        } catch (error) {
          log.error('Error parsing stored notifications:', error);
        }
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClearAll = () => {
    onClearNotifications();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => {
          refreshNotificationsFromStorage();
          setIsOpen(!isOpen);
        }}
        className="p-2 text-white/90 hover:text-white hover:bg-white/20 rounded-lg transition-all duration-200 hover:scale-110"
      >
        <FaBell className="w-5 h-5" />
        {notifications.filter(n => !n.isRead).length > 0 && (
          <span className="absolute -top-1 -right-1 bg-gradient-secondary text-white rounded-full text-xs w-5 h-5 flex items-center justify-center font-medium animate-bounce-slow">
            {notifications.filter(n => !n.isRead).length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-dark-card border border-dark-border rounded-lg shadow-lg z-50">
          <div className="p-4 border-b border-dark-border">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-white">Notifications</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    navigate('/notifications');
                    setIsOpen(false);
                  }}
                  className="text-sm text-snipehouse-blue hover:text-snipehouse-blue-dark transition-colors"
                >
                  Show all
                </button>
                {notifications.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    className="text-sm text-snipehouse-blue hover:text-snipehouse-blue-dark transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            <NotificationDropdown 
              notifications={notifications}
              updateNotifications={updateNotifications}
              onClose={() => setIsOpen(false)}
              limit={4}
            />
          </div>
        </div>
      )}
    </div>
  );
};

NotificationBell.propTypes = {
  notifications: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    message: PropTypes.string,
    createdAt: PropTypes.string,
  })).isRequired,
  onClearNotifications: PropTypes.func.isRequired,
  user: PropTypes.object,
  setNotifications: PropTypes.func.isRequired,
  updateNotifications: PropTypes.func.isRequired,
};

export default NotificationBell;